// MarkerLayer.js
//
// Operator-placed reference points, drawn over the imagery in planning-frame
// coordinates. These are REFERENCE geometry like the KML features — a dock, a gate,
// a surveyed stake — not navigation lines. They never enter lines/lineGroups and are
// deliberately non-interactive.
//
// Sizing follows KmlOverlay: measure the frame->screen ratio and express sizes as true
// SCREEN pixels. Sizing in frame units is what made the KML overlay invisible, because a
// synthesised frame packs tens of units per screen pixel.

import React, { useRef, useState, useLayoutEffect } from 'react';

export default function MarkerLayer({ markers, frame, currentScale = 1, position = null }) {
  const svgRef = useRef(null);
  const [unitsPerPx, setUnitsPerPx] = useState(null);

  useLayoutEffect(() => {
    const el = svgRef.current;
    if (!el || !frame) return;
    const w = el.getBoundingClientRect().width;   // post-transform: includes zoom
    if (w > 0) setUnitsPerPx(frame.width / w);
  }, [frame, currentScale, position, markers]);

  if (!markers || markers.length === 0 || !frame || !frame.lonLatToPixel) return null;
  const upp = unitsPerPx || frame.width / 1200;

  const r = 7 * upp;          // ring radius, ~7 screen px
  const sw = 2 * upp;         // stroke, ~2 screen px
  const fs = 13 * upp;        // label size

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${frame.width} ${frame.height}`}
      preserveAspectRatio="none"
      style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 2,   // above imagery AND the KML overlay
      }}
    >
      {markers.map((m) => {
        const [x, y] = frame.lonLatToPixel(m.lon, m.lat);
        const label = m.label || `${m.lat.toFixed(6)}, ${m.lon.toFixed(6)}`;
        const tw = label.length * fs * 0.55;
        return (
          <g key={m.id}>
            {/* crosshair + ring: the exact point is the centre, not the blob edge */}
            <line x1={x - r * 1.8} y1={y} x2={x + r * 1.8} y2={y} stroke="#000" strokeWidth={sw * 1.8} strokeOpacity="0.45" />
            <line x1={x} y1={y - r * 1.8} x2={x} y2={y + r * 1.8} stroke="#000" strokeWidth={sw * 1.8} strokeOpacity="0.45" />
            <line x1={x - r * 1.8} y1={y} x2={x + r * 1.8} y2={y} stroke="#00e5ff" strokeWidth={sw} />
            <line x1={x} y1={y - r * 1.8} x2={x} y2={y + r * 1.8} stroke="#00e5ff" strokeWidth={sw} />
            <circle cx={x} cy={y} r={r} fill="none" stroke="#00e5ff" strokeWidth={sw} />
            <circle cx={x} cy={y} r={sw} fill="#00e5ff" />
            <rect x={x + r * 2.2} y={y - fs * 0.95} width={tw} height={fs * 1.5}
                  rx={fs * 0.25} fill="#04222a" opacity="0.85" stroke="#00e5ff" strokeWidth={sw * 0.5} />
            <text x={x + r * 2.2 + tw / 2} y={y - fs * 0.2} fontSize={fs} fontWeight="600"
                  fill="#b8f4ff" textAnchor="middle" dominantBaseline="middle">{label}</text>
          </g>
        );
      })}
    </svg>
  );
}
